/**
 * SafePassage Design System
 * Based on Visual & Interaction Design Brief
 *
 * Design Philosophy: Clarity-Driven, Consequence-Aware, Professionally Refined
 */

// Color Palette - Infrastructure-grade seriousness
export const colors = {
  // Backgrounds
  primaryBg: 0x1e2a38,        // Deep slate - scene environments, night
  secondaryBg: 0xe8e4df,      // Warm concrete - day scenarios, UI panels
  panelBg: 0x2a3847,          // Slightly lighter slate for panels

  // Accents
  neutral: 0x5c7a99,          // Muted blue-grey - inactive elements
  neutralLight: 0x7a9bb8,     // Lighter neutral for hover states
  neutralDark: 0x3d5266,      // Darker neutral for borders

  // Semantic Colors
  decision: 0xe8a83c,         // Amber - active decision points, attention
  decisionHover: 0xf0b84c,    // Lighter amber for hover
  decisionPress: 0xd09830,    // Darker amber for press

  safe: 0x2a8b7a,             // Deep teal - positive outcomes
  safeLight: 0x3aa08d,        // Lighter teal for glow

  risk: 0xc4635c,             // Muted coral - caution, near-miss
  riskLight: 0xd4756e,        // Lighter coral

  critical: 0xb84a43,         // Controlled red - true danger only

  // Text
  textPrimary: 0xffffff,      // Primary text on dark backgrounds
  textSecondary: 0xb8c4d0,    // Secondary text
  textMuted: 0x7a8a9a,        // Muted text, hints
  textDark: 0x1e2a38,         // Text on light backgrounds

  // UI Elements
  cardBg: 0x3a4857,           // Card background
  cardBgHover: 0x4a5867,      // Card hover state
  cardBorder: 0x5c7a99,       // Card border

  // Scene Elements
  road: 0x3a3f4a,             // Road surface
  roadMarking: 0x6a7080,      // Road markings
  sidewalk: 0x5a6070,         // Sidewalk
  sky: 0x2a3848,              // Night sky
  skyDay: 0x87a5c0,           // Day sky
} as const;

// Typography Scale
export const typography = {
  // Font family - geometric sans-serif
  fontFamily: '"Inter", "IBM Plex Sans", "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Sizes
  sizes: {
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
    '4xl': 40,
    '5xl': 48,
  },

  // Weights
  weights: {
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },

  // Letter spacing
  letterSpacing: {
    tight: -0.5,
    normal: 0,
    wide: 0.5,
    wider: 1,
    widest: 2,
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// Spacing Scale (in pixels)
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
  '4xl': 64,
} as const;

// Border Radii
export const radii = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

// Shadows (as PIXI-compatible drop shadow configs)
export const shadows = {
  sm: { blur: 4, alpha: 0.15, offset: { x: 0, y: 2 } },
  md: { blur: 8, alpha: 0.2, offset: { x: 0, y: 4 } },
  lg: { blur: 16, alpha: 0.25, offset: { x: 0, y: 8 } },
  glow: { blur: 20, alpha: 0.4, offset: { x: 0, y: 0 } },
} as const;

// Animation Durations (in milliseconds)
export const durations = {
  instant: 100,
  fast: 150,
  normal: 300,
  slow: 500,
  slower: 800,
  crossfade: 600,
  consequenceHold: 500,
  decisionTransition: 800,
} as const;

// Easing Functions
export const easings = {
  // Standard easing
  easeOut: (t: number) => 1 - Math.pow(1 - t, 3),
  easeIn: (t: number) => Math.pow(t, 3),
  easeInOut: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,

  // Subtle easing for UI
  easeOutQuad: (t: number) => 1 - (1 - t) * (1 - t),
  easeInQuad: (t: number) => t * t,

  // Smooth for scene transitions
  easeInOutSine: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
} as const;

// Z-Index Layers
export const layers = {
  background: 0,
  environment: 10,
  actors: 20,
  player: 30,
  overlays: 40,
  ui: 50,
  modals: 60,
  tooltips: 70,
} as const;

// Consequence Severity Styles
export const severityStyles = {
  positive: {
    bg: 0x1e3d35,
    border: colors.safe,
    accent: colors.safeLight,
    text: colors.safe,
  },
  neutral: {
    bg: colors.panelBg,
    border: colors.neutral,
    accent: colors.neutralLight,
    text: colors.textSecondary,
  },
  warning: {
    bg: 0x3d3525,
    border: colors.decision,
    accent: colors.decisionHover,
    text: colors.decision,
  },
  negative: {
    bg: 0x3d2525,
    border: colors.risk,
    accent: colors.riskLight,
    text: colors.risk,
  },
} as const;

// Progress Indicator Colors
export const progressColors = {
  completed: colors.safe,
  current: colors.decision,
  upcoming: colors.neutralDark,
} as const;

// Decision Card States
export const cardStates = {
  default: {
    bg: colors.cardBg,
    border: colors.neutralDark,
    shadow: shadows.sm,
  },
  hover: {
    bg: colors.cardBgHover,
    border: colors.neutral,
    shadow: shadows.md,
    translateY: -2,
  },
  pressed: {
    bg: colors.cardBgHover,
    border: colors.decision,
    shadow: shadows.sm,
    translateY: 1,
  },
  selected: {
    bg: 0x4a3d28,
    border: colors.decision,
    shadow: shadows.md,
  },
  disabled: {
    bg: colors.neutralDark,
    border: colors.neutralDark,
    shadow: shadows.sm,
    opacity: 0.5,
  },
} as const;

// Screen Edge Glow Colors (for consequence feedback)
export const glowColors = {
  safe: colors.safe,
  nearMiss: colors.decision,
  unsafe: colors.risk,
} as const;

// Decision Moment - Cinematic mode when choices appear
export const decisionMoment = {
  // Scene dimming during decision
  sceneDimAlpha: 0.08,

  // Spotlight on conflict area
  spotlightIntensity: 1.15,

  // Vignette strength
  vignetteAlpha: 0.35,
  vignetteSize: 0.4, // Percentage of screen for gradient

  // Animation timing
  transitionDuration: 400,
  slowMotionFactor: 0.3,

  // Card reveal stagger
  cardStaggerDelay: 80,
} as const;

// Risk encoding for decision cards (subtle visual cues)
export const riskEncoding = {
  safe: {
    accentColor: colors.safe,
    pulseSpeed: 0, // No pulse - stable
    borderGlow: 0,
  },
  caution: {
    accentColor: colors.decision,
    pulseSpeed: 2000, // Slow pulse
    borderGlow: 0.15,
  },
  risky: {
    accentColor: colors.risk,
    pulseSpeed: 1200, // Faster pulse
    borderGlow: 0.25,
  },
  neutral: {
    accentColor: colors.neutral,
    pulseSpeed: 0,
    borderGlow: 0,
  },
} as const;

// Consequence animations (keyed by ConsequenceSeverity)
export const consequenceEffects = {
  positive: {
    duration: 600,
    sceneTransition: 'smooth', // Smooth continuation
    glowColor: colors.safe,
    glowIntensity: 0.3,
  },
  warning: {
    duration: 800,
    sceneTransition: 'decelerate', // Sharp slowdown
    glowColor: colors.decision,
    glowIntensity: 0.5,
    shakeIntensity: 3,
  },
  negative: {
    duration: 1000,
    sceneTransition: 'freeze', // Freeze + spotlight
    glowColor: colors.risk,
    glowIntensity: 0.6,
    shakeIntensity: 5,
  },
  neutral: {
    duration: 500,
    sceneTransition: 'smooth',
    glowColor: colors.neutral,
    glowIntensity: 0.2,
  },
} as const;

// Parallax layers for depth
export const parallax = {
  background: 0.02, // Slowest - buildings
  midground: 0.05,  // Medium - environment
  foreground: 0.1,  // Fastest - road markings
} as const;

// Helper function to convert hex to RGB components
export function hexToRgb(hex: number): { r: number; g: number; b: number } {
  return {
    r: (hex >> 16) & 0xff,
    g: (hex >> 8) & 0xff,
    b: hex & 0xff,
  };
}

// Helper function to interpolate between two colors
export function lerpColor(color1: number, color2: number, t: number): number {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = Math.round(c1.r + (c2.r - c1.r) * t);
  const g = Math.round(c1.g + (c2.g - c1.g) * t);
  const b = Math.round(c1.b + (c2.b - c1.b) * t);

  return (r << 16) | (g << 8) | b;
}

// Theme object for easy access
export const theme = {
  colors,
  typography,
  spacing,
  radii,
  shadows,
  durations,
  easings,
  layers,
  severityStyles,
  progressColors,
  cardStates,
  glowColors,
  decisionMoment,
  riskEncoding,
  consequenceEffects,
  parallax,
} as const;

export default theme;
